import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "sonner";
import { useTranslation } from "react-i18next";
import { hasAiFeatures, hasInventory } from "@/lib/planFeatures";

// Context Providers
import { AppProviders } from "@/contexts/AppProviders";
import { useAuth } from "@/contexts/AuthContext";

import MainLayout from "@/components/layout/MainLayout";
import UpgradeOverlay from "@/components/common/UpgradeOverlay";
import AssistantWidget from "@/components/assistant/AssistantWidget";
import { Page } from "@/types";

// Pagine Auth & Onboarding
import AuthPage from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import ResetPassword from "@/pages/ResetPassword";
import NewShop from "@/pages/NewShop";
import SubscriptionPlans from "@/pages/SubscriptionPlans";
import DeactivatedAccount from "@/pages/DeactivatedAccount";
import PaymentSuccess from "@/pages/PaymentSuccess";
import BookingResult from "@/pages/BookingResult";
import MobileAuthConfirmed from "@/pages/MobileAuthConfirmed";
import DownloadClientApp from "@/pages/DownloadClientApp";

import ReferralVoucher from "@/pages/ReferralVoucher";

// Pagine Dashboard
import Home from "@/pages/Home";
import Analytics from "@/pages/Analytics";
import Assistant from "@/pages/Assistant";
import Billing from "@/pages/Billing";
import Calendar from "@/pages/Calendar";
import Customers from "@/pages/Customers";
import ComingSoon from "@/pages/ComingSoon";
import MediaCreation from "@/pages/MediaCreation";
import Inventory from "@/pages/Inventory";
import Profile from "@/pages/Profile";
import Salon from "@/pages/Salon";
import Settings from "@/pages/Settings";
import Staff from "@/pages/Staff";

// --- COMPONENTS UTILI ---

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

// --- ROUTE GUARDS ---

// PUBLIC ROUTE
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading, organization, user } = useAuth();

  if (loading) return <FullScreenLoader />;

  if (isAuthenticated) {
    if (user?.user_metadata?.is_active === false) {
      return <Navigate to="/deactivated-account" replace />;
    }
    if (!organization) return <Navigate to="/new-shop" replace />;
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// NEW SHOP ROUTE
function NewShopRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading, organization, user } = useAuth();

  if (loading) return <FullScreenLoader />;

  if (isAuthenticated && user?.is_active === false) {
    return <Navigate to="/deactivated-account" replace />;
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (organization) return <Navigate to="/" replace />;

  return <>{children}</>;
}

// ADMIN ROUTE
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useAuth();

  if (!isAdmin) return <Navigate to="/" replace />;

  return <>{children}</>;
}

// FEATURE ROUTE: pagina disponibile solo dai piani indicati (vedi lib/planFeatures)
function FeatureRoute({ allowed, descriptionKey, children }: {
  allowed: (plan?: string | null) => boolean;
  descriptionKey: string;
  children: React.ReactNode;
}) {
  const { subscriptionPlan } = useAuth();
  const { t } = useTranslation();

  if (!allowed(subscriptionPlan)) return <UpgradeOverlay description={t(descriptionKey)} />;

  return <>{children}</>;
}

// RESET PASSWORD ROUTE
function ResetPasswordRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <FullScreenLoader />;

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return <>{children}</>;
}

// PROTECTED ROUTE
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading, organization, user, isPaid } = useAuth();

  if (loading) return <FullScreenLoader />;

  if (isAuthenticated && user?.is_active === false) {
    return <Navigate to="/deactivated-account" replace />;
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!organization) return <Navigate to="/new-shop" replace />;

  if (!isPaid) return <Navigate to="/subscription" replace />;

  return <>{children}</>;
}

// --- DASHBOARD SHELL ---
const DashboardShell: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { subscriptionPlan } = useAuth(); // Check subscription status

  const pageToPath: Record<string, string> = {
    [Page.HOME]: "/",
    [Page.CALENDAR]: "/calendar",
    [Page.CUSTOMERS]: "/customers",
    [Page.STAFF]: "/staff",
    [Page.ANALYTICS]: "/analytics",
    [Page.ASSISTANT]: "/assistant",
    [Page.SALON]: "/salon",
    [Page.BILLING]: "/billing",
    [Page.INVENTORY]: "/inventory",
    [Page.MEDIA]: "/media",
    [Page.PROFILE]: "/profile",
    [Page.SETTINGS]: "/settings",
    [Page.NOTIFICATIONS]: "/notifications",
  };

  const getPageFromPath = (path: string): Page => {
    if (path.startsWith("/calendar")) return Page.CALENDAR;
    if (path.startsWith("/customers")) return Page.CUSTOMERS;
    if (path.startsWith("/staff")) return Page.STAFF;
    if (path.startsWith("/analytics")) return Page.ANALYTICS;
    if (path.startsWith("/assistant")) return Page.ASSISTANT;
    if (path.startsWith("/salon")) return Page.SALON;
    if (path.startsWith("/billing")) return Page.BILLING;
    if (path.startsWith("/inventory")) return Page.INVENTORY;
    if (path.startsWith("/media")) return Page.MEDIA;
    if (path.startsWith("/profile")) return Page.PROFILE;
    if (path.startsWith("/settings")) return Page.SETTINGS;
    if (path.startsWith("/notifications")) return Page.NOTIFICATIONS;

    return Page.HOME;
  };

  const currentPath = location.pathname;
  const currentPage = getPageFromPath(currentPath);

  const handleNavigate = (page: Page) => {
    const targetPath = pageToPath[page];
    if (targetPath) {
      navigate(targetPath);
    } else {
      console.error(`Nessun percorso definito per la pagina: ${page}`);
      navigate("/");
    }
  };

  // CHECK RESTRICTED ACCESS
  const ALLOWED_PAGES_FREE = [
    Page.PROFILE,
    Page.SETTINGS,
    Page.SALON,
    Page.BILLING,
    Page.NOTIFICATIONS,
  ];

  const isRestricted = subscriptionPlan === 'visibility' && !ALLOWED_PAGES_FREE.includes(currentPage);

  return (
    <MainLayout currentPage={currentPage} onNavigate={handleNavigate}>
      {isRestricted ? <UpgradeOverlay /> : <Outlet />}
      {/* Assistente AI apribile da ogni pagina (bottone flottante, owner-only) */}
      {!isRestricted && <AssistantWidget />}
    </MainLayout>
  );
};

// --- APP ---

const App: React.FC = () => {
  return (
    <Router>
      <Toaster position="top-center" richColors closeButton />

      <AppProviders>
        <Routes>

          {/* --- PUBLIC ROUTES --- */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          {/* dovrebbe essere privata */}
          <Route path="/reset" element={
            <ResetPasswordRoute>
              <ResetPassword />
            </ResetPasswordRoute>
          } />
          <Route path="/deactivated-account" element={<DeactivatedAccount />} />
          <Route path="/mobile-auth-confirmed" element={<MobileAuthConfirmed />} />
          <Route path="/referral" element={<ReferralVoucher />} />
          <Route path="/booking-result" element={<BookingResult />} />
          <Route path="/subscription" element={<SubscriptionPlans />} />
          <Route path="/download/:salonId" element={<DownloadClientApp />} />

          {/* --- AUTH ROUTES --- */}
          <Route path="/login/:token?" element={
            <PublicRoute>
              <AuthPage />
            </PublicRoute>
          } />

          {/* --- ONBOARDING ROUTES --- */}
          <Route path="/new-shop" element={
            <NewShopRoute>
              <NewShop />
            </NewShopRoute>
          } />

          {/* --- PROTECTED ROUTES --- */}
          <Route path="payment-success" element={
            <ProtectedRoute>
              <PaymentSuccess />
            </ProtectedRoute>
          } />

          <Route path="/" element={
            <ProtectedRoute>
              <DashboardShell />
            </ProtectedRoute>
          }>
            <Route index element={<Home />} />
            <Route path="analytics" element={
              <AdminRoute>
                <Analytics />
              </AdminRoute>
            } />
            <Route path="assistant" element={
              <AdminRoute>
                <FeatureRoute allowed={hasAiFeatures} descriptionKey="upgrade.desc_ai">
                  <Assistant />
                </FeatureRoute>
              </AdminRoute>
            } />
            <Route path="billing" element={
              <AdminRoute>
                <Billing />
              </AdminRoute>
            } />
            <Route path="calendar" element={<Calendar />} />
            <Route path="customers" element={<Customers />} />
            <Route path="media" element={<MediaCreation />} />
            <Route path="inventory" element={
              <FeatureRoute allowed={hasInventory} descriptionKey="upgrade.desc_inventory">
                <Inventory />
              </FeatureRoute>
            } />
            <Route path="profile" element={<Profile />} />
            <Route path="salon" element={<Salon />} />
            <Route path="settings" element={<Settings />} />
            <Route path="staff" element={<Staff />} />
          </Route>

          {/* CATCH ALL */}
          <Route path="*" element={<Navigate to="/" replace />} />

        </Routes>
      </AppProviders>
    </Router>
  );
};

export default App;